import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { Menu, X, ClipboardList, Activity, AlertTriangle, BookOpen, Box, Boxes, ChevronDown, ChevronRight, Euro, FileText, Gauge, Globe2, Layers, LayoutDashboard, ListChecks, Loader2, LogOut, MessageSquarePlus, Moon, Package, PanelLeftClose, PanelLeftOpen, ReceiptText, Scale, ShoppingCart, Sparkles, Sun, TrendingUp, Truck, User, Fuel, FlaskConical, SlidersHorizontal } from 'lucide-react';
import logoSvg from '/logo.svg';

function RunningShippy({ onDone }) {
  const [phase, setPhase] = useState('run-in');   // run-in → dance → run-out
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleAnimationEnd = () => {
    if (phase === 'run-in') {
      setPhase('dance');
      timerRef.current = setTimeout(() => setPhase('run-out'), 2400);
    } else if (phase === 'run-out') {
      onDone();
    }
  };

  const animStyle = phase === 'run-in'
    ? { left: '-80px', top: '50%', animation: 'shippy-run-in 2s ease-out forwards' }
    : phase === 'dance'
    ? { left: 'calc(50vw - 32px)', top: '50%', transform: 'translateY(-50%)' }
    : { left: 'calc(50vw - 32px)', top: '50%', animation: 'shippy-run-out 2s ease-in forwards' };

  const isRunning = phase === 'run-in' || phase === 'run-out';
  const legDur = isRunning ? '0.3s' : '0.6s';
  const legAngle = isRunning ? 20 : 8;
  const bounceDur = isRunning ? '0.3s' : '0.5s';
  const bounceY = isRunning ? -5 : -4;

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={animStyle}
      onAnimationEnd={handleAnimationEnd}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width={64} height={64}>
        <defs>
          <linearGradient id="rt" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#93C5FD" /><stop offset="100%" stopColor="#60A5FA" />
          </linearGradient>
          <linearGradient id="rf" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" /><stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
          <linearGradient id="rs" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#60A5FA" /><stop offset="100%" stopColor="#3B82F6" />
          </linearGradient>
          <linearGradient id="cape" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#EF4444" /><stop offset="100%" stopColor="#B91C1C" />
          </linearGradient>
        </defs>
        {/* Left leg */}
        <rect x="30" y="72" width="8" height="16" rx="4" fill="#1E40AF">
          <animateTransform attributeName="transform" type="rotate" values={`-${legAngle},34,72;${legAngle},34,72;-${legAngle},34,72`} dur={legDur} repeatCount="indefinite" />
        </rect>
        <ellipse cx="34" cy="90" rx="8" ry="4" fill="#1D4ED8">
          <animateTransform attributeName="transform" type="rotate" values={`-${legAngle},34,72;${legAngle},34,72;-${legAngle},34,72`} dur={legDur} repeatCount="indefinite" />
        </ellipse>
        {/* Right leg */}
        <rect x="54" y="72" width="8" height="16" rx="4" fill="#1E40AF">
          <animateTransform attributeName="transform" type="rotate" values={`${legAngle},58,72;-${legAngle},58,72;${legAngle},58,72`} dur={legDur} repeatCount="indefinite" />
        </rect>
        <ellipse cx="58" cy="90" rx="8" ry="4" fill="#1D4ED8">
          <animateTransform attributeName="transform" type="rotate" values={`${legAngle},58,72;-${legAngle},58,72;${legAngle},58,72`} dur={legDur} repeatCount="indefinite" />
        </ellipse>
        {/* Body — bounce */}
        <g>
          <animateTransform attributeName="transform" type="translate" values={`0,0;0,${bounceY};0,0`} dur={bounceDur} repeatCount="indefinite" />
          {/* Left arm — pumping while running, waving while dancing */}
          <g>
            {phase === 'dance' ? (
              <animateTransform attributeName="transform" type="rotate" values="0,20,48;-35,20,48;0,20,48" dur="0.7s" repeatCount="indefinite" />
            ) : (
              <animateTransform attributeName="transform" type="rotate" values="15,20,48;-25,20,48;15,20,48" dur="0.3s" repeatCount="indefinite" />
            )}
            <path d="M20 48 Q10 38, 8 26 Q7 20, 12 18" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
            <circle cx="12" cy="17" r="5" fill="#FCD34D" />
          </g>
          {/* Right arm */}
          <g>
            {phase === 'dance' ? (
              <animateTransform attributeName="transform" type="rotate" values="0,72,48;30,72,48;0,72,48" dur="0.7s" repeatCount="indefinite" />
            ) : (
              <animateTransform attributeName="transform" type="rotate" values="-15,72,48;25,72,48;-15,72,48" dur="0.3s" repeatCount="indefinite" />
            )}
            <path d="M72 48 Q82 38, 84 26 Q85 20, 80 18" fill="none" stroke="#1E40AF" strokeWidth="7" strokeLinecap="round" />
            <circle cx="80" cy="17" r="5" fill="#FCD34D" />
          </g>
          {/* Cape — flows behind the box */}
          {isRunning ? (
            <path d="M76 28 Q92 40, 96 60 Q98 72, 88 78 Q80 68, 76 50 Z" fill="url(#cape)" opacity="0.9">
              <animate attributeName="d"
                values="M76 28 Q92 40, 96 60 Q98 72, 88 78 Q80 68, 76 50 Z;
                        M76 28 Q94 36, 98 55 Q100 68, 92 75 Q82 64, 76 50 Z;
                        M76 28 Q92 40, 96 60 Q98 72, 88 78 Q80 68, 76 50 Z"
                dur="0.3s" repeatCount="indefinite" />
            </path>
          ) : (
            <path d="M76 28 Q86 38, 84 55 Q82 68, 78 72 Q76 62, 76 50 Z" fill="url(#cape)" opacity="0.9">
              <animate attributeName="d"
                values="M76 28 Q86 38, 84 55 Q82 68, 78 72 Q76 62, 76 50 Z;
                        M76 28 Q88 36, 86 52 Q84 65, 80 70 Q76 60, 76 50 Z;
                        M76 28 Q86 38, 84 55 Q82 68, 78 72 Q76 62, 76 50 Z"
                dur="0.7s" repeatCount="indefinite" />
            </path>
          )}
          {/* Box body */}
          <path d="M22 30 L46 18 L76 30 L52 42 Z" fill="url(#rt)" stroke="#1E40AF" strokeWidth="1.5" />
          <line x1="46" y1="18" x2="52" y2="42" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
          <path d="M22 30 L52 42 L52 75 L22 63 Z" fill="url(#rf)" stroke="#1E40AF" strokeWidth="1.5" />
          <path d="M76 30 L52 42 L52 75 L76 63 Z" fill="url(#rs)" stroke="#1E40AF" strokeWidth="1.5" />
          <line x1="37" y1="36" x2="37" y2="69" stroke="#FCD34D" strokeWidth="3" strokeLinecap="round" opacity="0.8" />
          {/* Eyes */}
          <ellipse cx="33" cy="48" rx="3.5" ry="4" fill="white" />
          <ellipse cx="45" cy="46" rx="3.5" ry="4" fill="white" />
          <ellipse cx="35" cy="47.5" rx="2" ry="2.5" fill="#1E293B" />
          <ellipse cx="47" cy="45.5" rx="2" ry="2.5" fill="#1E293B" />
          <circle cx="36" cy="46" r="1" fill="white" />
          <circle cx="48" cy="44" r="1" fill="white" />
          {/* Smile */}
          <path d={phase === 'dance' ? "M30 54 Q38 62, 48 54" : "M30 54 Q38 60, 48 54"} fill="none" stroke="#1E293B" strokeWidth="2.5" strokeLinecap="round" />
          {/* Rosy cheeks */}
          <ellipse cx="28" cy="55" rx="3" ry="2" fill="#F9A8D4" opacity="0.6" />
          <ellipse cx="50" cy="53" rx="3" ry="2" fill="#F9A8D4" opacity="0.6" />
        </g>
      </svg>
    </div>
  );
}
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import axiosInstance from '../utils/axiosInstance';

const navSections = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    ],
  },
  {
    label: 'Packing',
    items: [
      { label: 'Packing Simulator', icon: Package, path: '/packing-simulator' },
      { label: 'OUT Packing', icon: Truck, path: '/out-packing' },
      { label: 'Boxopedia', icon: Box, path: '/packaging-data' },
      { label: 'Box Inventory', icon: Boxes, path: '/inventory' },
    ],
  },
  {
    label: 'Rates Management',
    items: [
      { label: 'Shipping Config', icon: Euro, path: '/shipping-rates' },
      { label: 'Pricing Rules', icon: SlidersHorizontal, path: '/pricing-rules' },
      { label: 'Fuel Index', icon: Fuel, path: '/fuel-index' },
      { label: 'Experiments', icon: FlaskConical, path: '/experiments' },
      {
        label: 'Invoice Analysis', icon: ReceiptText, path: '/carrier-invoices',
        children: [
          { label: 'Invoices', icon: FileText, path: '/carrier-invoices' },
          { label: 'To check', icon: ListChecks, path: '/carrier-invoices/worklist' },
          { label: 'Activity', icon: Activity, path: '/carrier-invoices/activity' },
          { label: 'Carriers', icon: BookOpen, path: '/carrier-invoices/carriers' },
          { label: 'Rate decisions', icon: Scale, path: '/carrier-invoices/decisions' },
        ],
      },
    ],
  },
  {
    label: 'Coverage',
    items: [
      { label: 'Zones', icon: Layers, path: '/zones' },
      { label: 'Coverage Map', icon: Globe2, path: '/coverage-map' },
    ],
  },
  {
    label: 'Performance & Testing',
    items: [
      { label: 'Benchmark', icon: Gauge, path: '/benchmark' },
      { label: 'API Health', icon: Activity, path: '/api-health' },
      { label: 'Bugs & Features', icon: MessageSquarePlus, path: '/bugs-features' },
    ],
  },
  {
    label: 'Documentation',
    items: [
      { label: 'Algorithm Guide', icon: BookOpen, path: '/algorithm-guide' },
      { label: 'Checkout Guide', icon: ShoppingCart, path: '/checkout-guide' },
      { label: 'Shippy in Claude', icon: Sparkles, path: '/claude-connector' },
      // { label: 'Franco Analysis', icon: TrendingUp, path: '/franco-analysis' }, // archived — route still reachable via direct URL
    ],
  },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);   // phone: sidebar as a drawer
  // A nav group remembers being opened or shut; until someone touches it, it
  // follows the route, so landing deep inside a group never hides where you are.
  const [openGroups, setOpenGroups] = useState({});
  const [shippyRunning, setShippyRunning] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  });
  const [boxopediaSync, setBoxopediaSync] = useState({ lastSyncAt: null, syncing: false });
  const [openTicketCount, setOpenTicketCount] = useState(0);

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  // Fetch open bugs/features count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await axiosInstance.get('api/feature-requests/');
        const open = res.data.filter(r => r.status === 'open' || r.status === 'in_progress').length;
        setOpenTicketCount(open);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Boxopedia freshness
  const fetchFreshness = useCallback(async () => {
    try {
      const res = await axiosInstance.get('api/packaging-data/freshness/');
      setBoxopediaSync(prev => ({ ...prev, lastSyncAt: res.data.last_sync_at }));
    } catch {}
  }, []);

  useEffect(() => {
    fetchFreshness();
    const interval = setInterval(fetchFreshness, 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [fetchFreshness]);

  const handleBoxopediaSync = async (e) => {
    e.stopPropagation();
    if (boxopediaSync.syncing) return;
    setBoxopediaSync(prev => ({ ...prev, syncing: true }));
    try {
      await axiosInstance.post('api/packaging-data/sync/');
      await fetchFreshness();
    } catch {}
    setBoxopediaSync(prev => ({ ...prev, syncing: false }));
  };

  const getBoxopediaFreshness = () => {
    if (!boxopediaSync.lastSyncAt) return { color: 'bg-gray-400', label: 'Never synced' };
    const hours = (Date.now() - new Date(boxopediaSync.lastSyncAt).getTime()) / 3600000;
    if (hours < 1) return { color: 'bg-emerald-500', label: 'Synced just now' };
    if (hours < 24) return { color: 'bg-emerald-500', label: `Synced ${Math.round(hours)}h ago` };
    const days = Math.round(hours / 24);
    if (days <= 3) return { color: 'bg-yellow-500', label: `Synced ${days}d ago` };
    return { color: 'bg-red-500', label: `Synced ${days}d ago` };
  };

  const getInitials = (username) => {
    return username ? username.slice(0, 2).toUpperCase() : 'U';
  };

  return (
    <div className="h-screen bg-background text-foreground flex overflow-hidden">
      {/* Phone: the sidebar is a drawer over the page. From 768px up nothing changes: hidden -> md:flex, static widths. */}
      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} aria-hidden />}
      <aside className={`${mobileOpen ? 'fixed inset-y-0 left-0 z-40 flex w-64 shadow-2xl md:static md:z-auto md:shadow-none' : 'hidden md:flex'} ${collapsed && !mobileOpen ? 'md:w-20' : 'md:w-64'} border-r border-border/60 surface-panel backdrop-blur-sm flex-col justify-between transition-all duration-200`}>
        <div className="px-4 py-6 space-y-6">
          <div className="flex items-center justify-between px-2">
            {collapsed ? (
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => setCollapsed(false)}
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="absolute inset-0 -m-1 rounded-full bg-gradient-to-br from-brand/30 to-brand-2/20 blur-md" aria-hidden />
                  <img
                    src={logoSvg}
                    alt="Shippy Logo"
                    className="relative h-12 w-12 cursor-pointer hover:scale-110 active:scale-95 transition-transform duration-200"
                    onClick={() => { if (!shippyRunning) setShippyRunning(true); }}
                  />
                </div>
                <div className="flex flex-col leading-tight">
                  <p className="text-xl font-bold text-foreground tracking-tight">Shippy</p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/70">shipping intelligence</p>
                </div>
              </div>
            )}

            {!collapsed && (
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                onClick={() => setCollapsed(true)}
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>

          <nav className="space-y-5">
            {navSections.map((section) => (
              <div key={section.label}>
                {!collapsed && (
                  <div className="px-3 mb-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
                      {section.label}
                    </p>
                    <div className="section-divider" aria-hidden />
                  </div>
                )}
                {collapsed && <div className="border-t border-border/50 mx-2 mb-2" />}
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    if (item.children) {
                      const inGroup = location.pathname.startsWith(item.path);
                      const open = !collapsed && (openGroups[item.path] ?? inGroup);
                      const Chevron = open ? ChevronDown : ChevronRight;
                      return (
                        <div key={item.path}>
                          <div className="relative flex items-center gap-0.5">
                            {inGroup && !collapsed && <span className="brand-active-rail" aria-hidden />}
                            <button
                              type="button"
                              className={`
                                flex-1 inline-flex items-center gap-2.5 rounded-md ${collapsed ? 'justify-center px-2' : 'pl-3.5 pr-2.5'} py-2
                                text-sm transition-all duration-150 relative group
                                ${inGroup
                                  ? 'brand-pill-active'
                                  : 'text-foreground/75 hover:bg-muted/60 hover:text-foreground font-medium'}
                              `}
                              onClick={() => {
                                if (collapsed) return navigate(item.path);
                                setOpenGroups((g) => ({ ...g, [item.path]: !open }));
                                if (!inGroup) navigate(item.path);
                              }}
                              title={collapsed ? item.label : undefined}
                            >
                              <Icon className={`h-4 w-4 shrink-0 transition-colors duration-150 ${
                                inGroup ? 'text-brand' : 'text-muted-foreground group-hover:text-foreground'
                              }`} />
                              {!collapsed && (
                                <>
                                  <span className="truncate">{item.label}</span>
                                  <Chevron className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                </>
                              )}
                            </button>
                          </div>
                          {open && (
                            <div className="mt-0.5 ml-[1.45rem] border-l border-border/60 pl-1.5 space-y-0.5">
                              {item.children.map((child) => {
                                const ChildIcon = child.icon;
                                const childActive = location.pathname === child.path;
                                return (
                                  <button
                                    key={child.path}
                                    type="button"
                                    className={`
                                      w-full inline-flex items-center gap-2 rounded-md pl-2.5 pr-2 py-1.5 text-[13px]
                                      transition-colors duration-150 group
                                      ${childActive
                                        ? 'bg-muted text-foreground font-medium'
                                        : 'text-foreground/65 hover:bg-muted/50 hover:text-foreground'}
                                    `}
                                    onClick={() => navigate(child.path)}
                                  >
                                    <ChildIcon className={`h-3.5 w-3.5 shrink-0 ${
                                      childActive ? 'text-brand' : 'text-muted-foreground group-hover:text-foreground'
                                    }`} />
                                    <span className="truncate">{child.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    const isActive = location.pathname === item.path;
                    const isBoxopedia = item.path === '/packaging-data';
                    const isBugsFeatures = item.path === '/bugs-features';
                    const freshness = isBoxopedia ? getBoxopediaFreshness() : null;
                    return (
                      <div key={item.path} className="relative flex items-center gap-0.5">
                        {isActive && !collapsed && (
                          <span className="brand-active-rail" aria-hidden />
                        )}
                        <button
                          type="button"
                          className={`
                            flex-1 inline-flex items-center gap-2.5 rounded-md ${collapsed ? 'justify-center px-2' : 'pl-3.5 pr-2.5'} py-2
                            text-sm transition-all duration-150 relative group
                            ${isActive
                              ? 'brand-pill-active'
                              : 'text-foreground/75 hover:bg-muted/60 hover:text-foreground font-medium'}
                          `}
                          onClick={() => navigate(item.path)}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon className={`h-4 w-4 shrink-0 transition-colors duration-150 ${
                            isActive ? 'text-brand' : 'text-muted-foreground group-hover:text-foreground'
                          }`} />
                          {!collapsed && <span className="truncate">{item.label}</span>}
                          {isBugsFeatures && openTicketCount > 0 && !collapsed && (
                            <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                              {openTicketCount}
                            </span>
                          )}
                          {isBugsFeatures && openTicketCount > 0 && collapsed && (
                            <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-background" />
                          )}
                        </button>
                        {isBoxopedia && !collapsed && (
                          <button
                            onClick={handleBoxopediaSync}
                            disabled={boxopediaSync.syncing}
                            className="p-1.5 rounded-md hover:bg-accent transition-colors group relative"
                            title={boxopediaSync.syncing ? 'Syncing...' : `${freshness.label} — click to sync`}
                          >
                            {boxopediaSync.syncing ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : (
                              <span className={`block h-2.5 w-2.5 rounded-full ${freshness.color} ring-2 ring-background`} />
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </div>

        <div className={`${collapsed ? 'mx-2' : 'mx-3'} mb-4 rounded-xl border border-border/60 bg-muted/40 dark:bg-muted/20 backdrop-blur-sm p-1.5 space-y-1`}>
          <button
            type="button"
            className={`w-full inline-flex items-center ${collapsed ? 'justify-center' : 'gap-2.5 pl-3 pr-2'} py-2 rounded-md text-sm font-medium text-foreground/80 hover:bg-card hover:text-foreground transition-colors`}
            onClick={() => setDarkMode((prev) => !prev)}
            title={collapsed ? (darkMode ? 'Light mode' : 'Dark mode') : undefined}
          >
            {darkMode ? <Sun className="h-4 w-4 text-amber-400 shrink-0" /> : <Moon className="h-4 w-4 text-slate-500 shrink-0" />}
            {!collapsed && (darkMode ? 'Light mode' : 'Dark mode')}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={`w-full flex items-center ${collapsed ? 'justify-center' : 'gap-3 px-2'} py-1.5 rounded-md text-sm font-medium transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
                title={user?.username}
              >
                <Avatar className="h-8 w-8 ring-2 ring-brand/30">
                  {user?.profile?.avatar ? (
                    <AvatarImage src={user.profile.avatar} alt={user.username} />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-brand to-brand-2 text-white">{getInitials(user?.username)}</AvatarFallback>
                  )}
                </Avatar>
                {!collapsed && (
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-foreground text-sm">{user?.username}</span>
                    <span className="text-[10px] text-muted-foreground">Account</span>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{user?.username}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={logout} className="gap-2">
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Phone top bar (hidden from 768px up) */}
        <header className="md:hidden flex items-center gap-2 px-3 h-12 border-b border-border/60 surface-panel shrink-0">
          <button type="button" className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-accent" aria-label="Open menu" onClick={() => setMobileOpen(v => !v)}>
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <img src="/logo.svg" alt="" className="h-6 w-6" />
          <span className="font-semibold">Shippy</span>
        </header>
        <main className="flex-1 overflow-auto pb-16 md:pb-0">
          <Outlet />
        </main>
        {/* Phone bottom bar (hidden from 768px up) */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border/60 surface-panel grid grid-cols-4" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} aria-label="Mobile navigation">
          {[
            { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
            { label: 'Orders', icon: ClipboardList, path: '/m/orders' },
            { label: 'Simulator', icon: Package, path: '/m/simulate' },
            { label: 'Fuel', icon: Fuel, path: '/fuel-index' },
          ].map(item => {
            const active = location.pathname.startsWith(item.path);
            return (
              <button key={item.path} type="button" onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] ${active ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                <item.icon className="h-5 w-5" />
                {item.label}
              </button>
            );
          })}
        </nav>
      </div>

      {shippyRunning && <RunningShippy onDone={() => setShippyRunning(false)} />}

      <style>{`
        @keyframes shippy-run-in {
          0%   { left: -80px; transform: translateY(-50%); }
          100% { left: calc(50vw - 32px); transform: translateY(-50%); }
        }
        @keyframes shippy-run-out {
          0%   { left: calc(50vw - 32px); transform: translateY(-50%) scaleX(-1); }
          100% { left: -80px; transform: translateY(-50%) scaleX(-1); }
        }
      `}</style>
    </div>
  );
};

export default Layout;
